import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/ui/dataTable/DataTable";
import { Pagination } from "../../components/ui/pagination/Pagination";
import { StatusBadge } from "../../components/ui/statusBadge/StatusBadge";
import { PrioBadge } from "../../components/ui/prioBadge/PrioBadge";
import { EmptyState } from "../../components/ui/emptyState/EmptyState";
import { useModal } from "../../components/ui/modal/ModalProvider";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { useIncidents, useCreateIncident } from "./hooks/useIncidents";
import { useHelpRequests } from "./hooks/useHelpRequests";
import { useSupportPeople } from "./hooks/useSupportPeople";
import { CreateIncidentForm, ModalActions } from "./ticketView/TicketModals";
import { TicketFiche } from "./ticketView/TicketFiche";
import {
  HELP_REQUEST_STATUS,
  HELP_REQUEST_STATUS_OPTIONS,
  INCIDENT_PRIORITY_OPTIONS,
  SUPPORT_ENTITY_TYPE_OPTIONS,
  supportEntityTypeLabel,
} from "../../constants/supportStatus";
import { formatGraphqlError } from "../../utils/graphqlError";
import { relative } from "../../utils/format";

const PAGE_SIZE = 8;
const TAB_REQUESTS = "requests";
const ASSIGNEE_ME = "me";

const KPI_TILES = [
  { key: "openNb", label: "Nouveaux", ico: "fa-sparkles", status: HELP_REQUEST_STATUS.OPEN },
  { key: "inProgressNB", label: "En cours", ico: "fa-spinner", status: HELP_REQUEST_STATUS.IN_PROGRESS },
  { key: "waitingOnCustomerNB", label: "En attente client", ico: "fa-user-clock", status: HELP_REQUEST_STATUS.WAITING_ON_CUSTOMER },
  { key: "resolved", label: "Résolus", ico: "fa-check", status: HELP_REQUEST_STATUS.RESOLVED },
  { key: "closed", label: "Fermés", ico: "fa-lock", status: HELP_REQUEST_STATUS.CLOSED },
];

export function Tickets() {
  const { id } = useParams();
  if (id) return <TicketFiche id={id} />;
  return <TicketsList />;
}

function TicketsList() {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { assigneeOptions } = useSupportPeople();
  const { createIncident } = useCreateIncident();
  const tab = searchParams.get("tab") === TAB_REQUESTS ? TAB_REQUESTS : "incidents";

  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === TAB_REQUESTS) params.set("tab", TAB_REQUESTS);
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  };

  // MT_ADD_INCIDENT exige une demande d'aide existante (requestId).
  const openCreate = (initialRequestId) => {
    const valuesRef = { current: null };
    openModal({
      title: "Nouveau ticket",
      size: "lg",
      body: <CreateIncidentForm valuesRef={valuesRef} initialRequestId={initialRequestId} assigneeOptions={assigneeOptions} />,
      footer: (
        <ModalActions
          submitLabel="Créer le ticket"
          testId="tk-save"
          onSubmit={async () => {
            if (!valuesRef.current?.requestId) throw new Error("Sélectionnez une demande d'aide");
            const incident = await createIncident(valuesRef.current);
            toast("Ticket créé", "success");
            if (incident?.id) navigate("/tickets/" + incident.id);
          }}
        />
      ),
    });
  };

  useEffect(() => {
    if (searchParams.get("new")) openCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><a onClick={() => navigate("/dashboard")}>Dashboard</a><i className="fa-solid fa-chevron-right"></i>Tickets Support</div>
          <h1 className="page-title">Tickets Support</h1>
          <div className="page-sub">Gestion des incidents et demandes d'aide</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" data-testid="btn-new-ticket" onClick={() => openCreate()}><i className="fa-solid fa-plus"></i> Nouveau ticket</button>
        </div>
      </div>

      {tab === TAB_REQUESTS ? (
        <HelpRequestsTab tabs={<TicketsTabs tab={tab} onChange={setTab} />} onCreateIncident={openCreate} />
      ) : (
        <IncidentsTab tabs={<TicketsTabs tab={tab} onChange={setTab} />} />
      )}
    </>
  );
}

function TicketsTabs({ tab, onChange }) {
  return (
    <div className="tabs">
      <div className={`tab${tab !== TAB_REQUESTS ? " active" : ""}`} data-testid="tab-incidents" onClick={() => onChange("incidents")}>
        <i className="fa-solid fa-ticket"></i> Tickets
      </div>
      <div className={`tab${tab === TAB_REQUESTS ? " active" : ""}`} data-testid="tab-requests" onClick={() => onChange(TAB_REQUESTS)}>
        <i className="fa-solid fa-inbox"></i> Demandes d'aide
      </div>
    </div>
  );
}

function IncidentsTab({ tabs }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { myId, personName, assigneeOptions } = useSupportPeople();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [status, setStatus] = useState(searchParams.get("f") === "new" ? HELP_REQUEST_STATUS.OPEN : "");
  const [priority, setPriority] = useState("");
  const [entityType, setEntityType] = useState("");
  const [assignee, setAssignee] = useState("");
  const [page, setPage] = useState(1);

  const { incidents, kpi, totalElements, loading, error, refetch } = useIncidents({
    page,
    pageSize: PAGE_SIZE,
    status,
    priority,
    entityType,
    assigneeId: assignee === ASSIGNEE_ME ? myId : assignee,
  });

  const withReset = (setter) => (e) => { setter(e.target.value); setPage(1); };

  // L'API n'a pas de recherche texte : filtre local sur la page courante.
  const rows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return incidents
      .map(({ incident, request }) => ({ id: incident.id, incident, request: request || {} }))
      .filter(({ incident, request }) =>
        !qq || [incident.incidentRef, request.helpRequestRef, request.subject].some((v) => (v || "").toLowerCase().includes(qq))
      );
  }, [incidents, q]);

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        {KPI_TILES.map((t) => (
          <div className="kpi" key={t.key} style={{ cursor: "pointer" }} onClick={() => { setStatus(t.status); setPage(1); }} data-testid={`kpi-${t.key}`}>
            <div className="kpi-label"><i className={`fa-solid ${t.ico}`}></i> {t.label}</div>
            <div className="kpi-value">{kpi ? kpi[t.key] ?? 0 : "—"}</div>
          </div>
        ))}
      </div>

      <div className="card">
        {tabs}
        <div className="toolbar">
          <div className="search"><i className="fa-solid fa-magnifying-glass"></i><input data-testid="tk-search" placeholder="Filtrer la page : n° de ticket, de demande, objet…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <select className="select" data-testid="tk-statut" value={status} onChange={withReset(setStatus)}>
            <option value="">Tous les statuts</option>
            {HELP_REQUEST_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select className="select" data-testid="tk-prio" value={priority} onChange={withReset(setPriority)}>
            <option value="">Toutes priorités</option>
            {INCIDENT_PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <select className="select" data-testid="tk-type" value={entityType} onChange={withReset(setEntityType)}>
            <option value="">Tous les types</option>
            {SUPPORT_ENTITY_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select className="select" data-testid="tk-agent" value={assignee} onChange={withReset(setAssignee)}>
            <option value="">Tous les agents</option>
            <option value={ASSIGNEE_ME}>Mes tickets</option>
            {assigneeOptions.filter((a) => a.id !== myId).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <div className="filter-chips"><span className="badge outline"><i className="fa-solid fa-filter"></i> {totalElements}</span></div>
        </div>

        {error && !incidents.length ? (
          <ListError error={error} onRetry={refetch} />
        ) : (
          <DataTable
            columns={[
              { label: "N°", render: (r) => <span className="mono strong">{r.incident.incidentRef}</span> },
              { label: "Objet", render: (r) => <div><div className="strong">{r.request.subject || "—"}</div><div className="muted">{r.request.helpRequestRef} · {supportEntityTypeLabel(r.request.entityType)}</div></div> },
              { label: "Agent", render: (r) => personName(r.incident.assigneeId) || <span className="badge outline">Non assigné</span> },
              { label: "Priorité", render: (r) => <PrioBadge priorite={r.incident.priority} /> },
              { label: "Statut", render: (r) => <StatusBadge statut={r.request.status} domain="support" /> },
              { label: "Créé", render: (r) => relative(r.incident.createdAt) },
              { label: "Dernière activité", render: (r) => relative(r.incident.updatedAt || r.incident.createdAt) },
            ]}
            rows={rows}
            empty={loading ? "Chargement…" : "Aucun ticket"}
            onRowClick={(r) => navigate("/tickets/" + r.id)}
            rowTestIdPrefix="tk-row"
          />
        )}
        <Pagination total={totalElements} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} />
      </div>
    </>
  );
}

function HelpRequestsTab({ tabs, onCreateIncident }) {
  const { personName } = useSupportPeople();
  const [status, setStatus] = useState(HELP_REQUEST_STATUS.OPEN);
  const [entityType, setEntityType] = useState("");
  const [page, setPage] = useState(1);

  const { helpRequests, totalElements, loading, error, refetch } = useHelpRequests({ page, pageSize: PAGE_SIZE, status, entityType });

  const withReset = (setter) => (e) => { setter(e.target.value); setPage(1); };

  return (
    <div className="card">
      {tabs}
      <div className="toolbar">
        <select className="select" data-testid="hr-statut" value={status} onChange={withReset(setStatus)}>
          <option value="">Tous les statuts</option>
          {HELP_REQUEST_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className="select" data-testid="hr-type" value={entityType} onChange={withReset(setEntityType)}>
          <option value="">Tous les types</option>
          {SUPPORT_ENTITY_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div className="filter-chips"><span className="badge outline"><i className="fa-solid fa-filter"></i> {totalElements}</span></div>
      </div>

      {error && !helpRequests.length ? (
        <ListError error={error} onRetry={refetch} />
      ) : (
        <DataTable
          columns={[
            { label: "Réf.", render: (r) => <span className="mono strong">{r.helpRequestRef || "—"}</span> },
            { label: "Objet", render: (r) => <div><div className="strong">{r.subject}</div><div className="muted" style={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.content}</div></div> },
            { label: "Type", render: (r) => <div>{supportEntityTypeLabel(r.entityType)}{r.entityId ? <div className="muted mono">{r.entityId}</div> : null}</div> },
            { label: "Demandeur", render: (r) => personName(r.requesterId) || "—" },
            { label: "Statut", render: (r) => <StatusBadge statut={r.status} domain="support" /> },
            { label: "Créée", render: (r) => relative(r.createdAt) },
            {
              label: "",
              render: (r) => (
                <button className="btn btn-sm" data-testid={`hr-create-${r.id}`} onClick={(e) => { e.stopPropagation(); onCreateIncident(r.id); }}>
                  <i className="fa-solid fa-ticket"></i> Créer un ticket
                </button>
              ),
            },
          ]}
          rows={helpRequests}
          empty={loading ? "Chargement…" : "Aucune demande d'aide"}
          onRowClick={(r) => onCreateIncident(r.id)}
          rowTestIdPrefix="hr-row"
        />
      )}
      <Pagination total={totalElements} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} />
    </div>
  );
}

function ListError({ error, onRetry }) {
  return (
    <div style={{ padding: 16 }}>
      <EmptyState title="Impossible de charger les données" sub={formatGraphqlError(error)} />
      <div style={{ textAlign: "center" }}>
        <button className="btn" onClick={() => onRetry()}><i className="fa-solid fa-rotate"></i> Réessayer</button>
      </div>
    </div>
  );
}

export default Tickets;
